import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { Button } from '@/components/Button/Button';
import { MODAL_TIMEOUT } from '@/config/constants';
import { AnalyticsEvent, useAnalytics } from '@/hooks/useAnalytics';
import { useCountdown } from '@/hooks/useCountdown';
import { useSocket } from '@/hooks/useSocket';
import { useWidgetState } from '@/hooks/useWidgetState';
import { chatModalState, sessionState, shouldDisplaySurveySelector, lastBrainResponseDefaultSurveySelector, afterSurveyAction, } from '@/state/session';
import { formatMinutesSecondsMMSS, sleep } from '@/util/utils';
import styles from './Resolved.module.scss';
export const Resolved = () => {
    const { t } = useTranslation();
    const { trackEvent } = useAnalytics();
    const { closeWidget } = useWidgetState();
    const { reopenConversation, closeSession } = useSocket();
    const [session, setSessionState] = useRecoilState(sessionState);
    const keep_alive = session === null || session === void 0 ? void 0 : session.keep_alive;
    const remaining_keep_alive = session === null || session === void 0 ? void 0 : session.remaining_keep_alive;
    const { minutes, seconds } = useCountdown(remaining_keep_alive !== null && remaining_keep_alive !== void 0 ? remaining_keep_alive : keep_alive);
    const shouldDisplaySurvey = useRecoilValue(shouldDisplaySurveySelector);
    const hasResponseSurvey = useRecoilValue(lastBrainResponseDefaultSurveySelector);
    const setChatModal = useSetRecoilState(chatModalState);
    const setAfterSurveyAction = useSetRecoilState(afterSurveyAction);
    const handleClose = useCallback(async () => {
        setChatModal(null);
        trackEvent(AnalyticsEvent.PopoverResolvedEndChatClicked);
        const clickAction = () => {
            closeSession();
            closeWidget();
        };
        await sleep(MODAL_TIMEOUT);
        if (shouldDisplaySurvey) {
            setAfterSurveyAction(() => clickAction);
            setChatModal("survey" /* ChatModal.Survey */);
        }
        else {
            clickAction();
        }
    }, [
        setChatModal,
        trackEvent,
        shouldDisplaySurvey,
        closeSession,
        closeWidget,
        setAfterSurveyAction,
    ]);
    const handleCancel = useCallback(async () => {
        trackEvent(AnalyticsEvent.PopoverResolvedContinueClicked);
        // Show survey only if there's a survey action from the response
        if (hasResponseSurvey) {
            setChatModal(null);
            const clickAction = () => {
                reopenConversation();
            };
            await sleep(MODAL_TIMEOUT);
            setAfterSurveyAction(() => clickAction);
            setChatModal("survey" /* ChatModal.Survey */);
        }
        else {
            reopenConversation();
            setChatModal(null);
        }
    }, [
        reopenConversation,
        setChatModal,
        trackEvent,
        hasResponseSurvey,
        setAfterSurveyAction,
    ]);
    useEffect(() => {
        (async () => {
            if (minutes === 0 && seconds === 0) {
                setChatModal(null);
                await sleep(MODAL_TIMEOUT);
                setSessionState((prev) => (Object.assign(Object.assign({}, prev), { status: "expired" /* SessionStatus.Expired */ })));
                setChatModal("expired" /* ChatModal.Expired */);
            }
        })();
    }, [minutes, seconds, setChatModal, setSessionState]);
    const [formattedMinutes, formattedSeconds] = formatMinutesSecondsMMSS(minutes, seconds).split(':');
    return (_jsxs("div", { className: styles.form, children: [_jsx("div", { className: styles.title, children: _jsx("span", { children: t('modal.resolved_old') }) }), _jsxs("div", { className: styles.secondContainer, children: [_jsx("p", { className: styles.subtitle, children: t('modal.continue') }), _jsx("div", { className: styles.countdownWrapper, children: _jsxs("div", { className: styles.countdown, children: [_jsx("span", { className: styles.box, children: formattedMinutes }), _jsx("span", { className: styles.separator, children: ":" }), _jsx("span", { className: styles.box, children: formattedSeconds }), _jsx("span", { className: styles.time, children: t('overview.mins') }), _jsx("span", {}), _jsx("span", { className: styles.time, children: t('overview.secs') })] }) })] }), _jsxs("div", { className: styles.buttonContainer, children: [_jsx(Button, { onClick: handleClose, children: t('buttons.end_chat') }), _jsx(Button, { onClick: handleCancel, variant: "secondary", children: t('buttons.continue') })] })] }));
};
